import { onMounted, onUnmounted, ref } from 'vue'
import { useEditorStore } from '@/stores/editor'
import { useCollectionsStore } from '@/stores/collections'
import { entwurfOffen } from '@/composables/useEntwurf'

/**
 * Selbst-Aktualisierung: das Panel merkt, wenn eine neue Fassung ausgeliefert
 * wurde, und holt sie sich.
 *
 * Deploye ich einen Fix, läuft in einem offenen Panel weiter die ALTE Fassung,
 * bis jemand neu lädt. Nahm und Davids Frau haben das CMS den ganzen Tag offen.
 *
 * WIE ERKANNT WIRD: Vite hängt an jede gebaute Datei einen Hash
 * (assets/index-AbC123.js). Das Panel lädt seine eigene index.html neu (ohne
 * Cache) und vergleicht den Namen des Haupt-Skripts mit dem, mit dem es selbst
 * gestartet ist. Anderer Name = neuer Build. Braucht keinen Endpunkt und keine
 * Zahl, die beim Deploy gepflegt werden müsste.
 *
 * WAS DANN PASSIERT (Davids Entscheidung 2026-07-24, wie in miraBOOK):
 *   - Niemand sieht hin (Tab versteckt) UND nichts Ungespeichertes offen
 *     → still neu laden. Beim Zurückkommen ist alles aktuell.
 *   - Jemand arbeitet gerade → dezenter Hinweis, er entscheidet wann.
 *
 * Ungespeicherte Änderungen wiegen hier besonders schwer: im CMS tippt jemand
 * ganze Texte. Deshalb wird der Editor- und der Katalog-Store gefragt, bevor
 * überhaupt still neu geladen wird.
 */

const TAKT_MS = 90_000

/** Name des Haupt-Skripts, mit dem diese Seite gestartet ist. */
function eigenesSkript(): string {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src]')
  return s ? new URL(s.src, location.href).pathname : ''
}

export function useVersionWatch() {
  const neueFassungDa = ref(false)
  const editor = useEditorStore()
  const collections = useCollectionsStore()

  let timer: number | undefined
  const start = eigenesSkript()

  /**
   * Tippt gerade jemand? Der Editor kennt ungespeicherte Felder (isDirty), der
   * Katalog noch nicht veröffentlichte Änderungen (dirty), und alles andere mit
   * Eingaben meldet sich über useEntwurf (Kommentare, Antworten, Formulare).
   * In jedem dieser Fälle wird NICHT still neu geladen — die Arbeit wäre weg.
   */
  function etwasUngespeichert(): boolean {
    return editor.isDirty || collections.dirty || entwurfOffen()
  }

  function anwenden() {
    location.reload()
  }

  async function pruefen() {
    if (neueFassungDa.value || start === '') return
    try {
      const res = await fetch(`${import.meta.env.BASE_URL || '/'}?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      })
      if (!res.ok) return
      const html = await res.text()
      const treffer = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/)
      if (!treffer?.[1]) return
      const aktuell = new URL(treffer[1], location.href).pathname
      if (aktuell === start) return

      neueFassungDa.value = true
      if (document.hidden && !etwasUngespeichert()) anwenden()
    } catch {
      // Netz weg → einfach weiterarbeiten.
    }
  }

  function beiSichtwechsel() {
    if (document.hidden && neueFassungDa.value && !etwasUngespeichert()) anwenden()
  }

  onMounted(() => {
    timer = window.setInterval(pruefen, TAKT_MS)
    document.addEventListener('visibilitychange', beiSichtwechsel)
  })

  onUnmounted(() => {
    if (timer) window.clearInterval(timer)
    document.removeEventListener('visibilitychange', beiSichtwechsel)
  })

  return { neueFassungDa, anwenden }
}
